import type {
  BuilderSection,
  SectionTemplateKey,
  WebsiteBuilderPage,
  WebsiteBuilderState,
} from 'types/websiteBuilder';
import galleryPlaceholder from 'assets/websiteBuilderDefaults/gallery-placeholder.svg';
import heroPlaceholder from 'assets/websiteBuilderDefaults/hero-placeholder.svg';
import avatarPlaceholder from 'assets/websiteBuilderDefaults/avatar-placeholder.svg';
import { ABRASOFT_HUB_URL } from 'utils/constants';

export type SectionCategory =
  'Navigation' | 'Main Content' | 'Gallery & Media' | 'Connect' | 'Other';

export interface TemplateDef {
  key: SectionTemplateKey;
  label: string;
  description: string;
  category: SectionCategory;
  iconName: string;
}

export const BUILDER_TEMPLATES: TemplateDef[] = [
  {
    key: 'header',
    label: 'Header',
    description: 'Logo and navigation menu for your page',
    category: 'Navigation',
    iconName: 'Layout',
  },
  {
    key: 'body',
    label: 'Body',
    description: 'Main page content (unique per page)',
    category: 'Main Content',
    iconName: 'FileText',
  },
  {
    key: 'hero',
    label: 'Hero',
    description: 'Eye-catching header with title, subtitle and CTA',
    category: 'Main Content',
    iconName: 'Layout',
  },
  {
    key: 'campaignHero',
    label: 'Campaign Hero',
    description: 'Full-screen campaign image (upload an image)',
    category: 'Main Content',
    iconName: 'Image',
  },
  {
    key: 'navigationTiles',
    label: 'Navigation Tiles',
    description:
      'Three image tiles with title, description, and a “Learn More” link',
    category: 'Main Content',
    iconName: 'Layout',
  },
  {
    key: 'marquee',
    label: 'Slider',
    description: 'Scrolling horizontal image slider',
    category: 'Gallery & Media',
    iconName: 'MoveHorizontal',
  },
  {
    key: 'features',
    label: 'Features',
    description: 'Showcase your product features in a grid',
    category: 'Main Content',
    iconName: 'Grid3X3',
  },
  {
    key: 'team',
    label: 'Team',
    description: 'Grid of team member cards',
    category: 'Main Content',
    iconName: 'Users',
  },
  {
    key: 'testimonials',
    label: 'Testimonials',
    description: 'Customer reviews and social proof',
    category: 'Main Content',
    iconName: 'MessageSquare',
  },
  {
    key: 'gallery',
    label: 'Gallery',
    description: 'Image gallery grid',
    category: 'Gallery & Media',
    iconName: 'Image',
  },
  {
    key: 'faq',
    label: 'FAQ',
    description: 'Accordion with common questions',
    category: 'Main Content',
    iconName: 'HelpCircle',
  },
  {
    key: 'cta',
    label: 'CTA',
    description: 'Call-to-action with title and button',
    category: 'Connect',
    iconName: 'Megaphone',
  },
  {
    key: 'footer',
    label: 'Footer',
    description: 'Links, copyright, social',
    category: 'Navigation',
    iconName: 'Footprints',
  },
];

function getDefaultData(type: SectionTemplateKey): Record<string, unknown> {
  switch (type) {
    case 'header':
      return {
        logoText: 'My Church',
        logoImageUrl: '',
        logoTextFontFamily: "'Trebuchet MS', 'Segoe UI', sans-serif",
        logoTextFontSize: 16,
        logoTextColor: '',
        logoAlignment: 'center',
        logoSizePx: 55,
        logoOffsetY: 0,
        logoTextBold: false,
        logoTextItalic: false,
        navItems: [],
        navFontFamily: "'Lato', Arial, sans-serif",
        navFontSize: 16,
        navTextColor: '',
        navBold: false,
        navItalic: false,
        backgroundOverlay: false,
      };
    case 'body':
      return {
        title: 'Page Content',
        subtitle:
          'Write your page content here. This section is unique for each page.',
        body: 'Write your page content here. This section is unique for each page.',
        menuImageUrl: heroPlaceholder,
        menuImageHeight: 320,
        menuShowImageBanner: true,
        menuImageBannerText: 'Beliefs',
      };
    case 'hero':
      return {
        title: 'Build\nSomething\nAmazing',
        subtitle: 'Simplify your workflow and get more done with less effort.',
        ctaText: 'Get Started',
        ctaLink: ABRASOFT_HUB_URL,
        ctaAlign: 'right',
        ctaFontFamily: '',
        ctaFontSize: 17,
        ctaOffsetY: 0,
        ctaColor: '',
        backgroundMediaType: 'image',
        backgroundImageUrl: heroPlaceholder,
        backgroundVideoUrl: '',
        backgroundVideoAssetId: '',
        titleFontSize: 60,
        titleFontFamily: "'Trebuchet MS', 'Segoe UI', sans-serif",
        titleOffsetY: 0,
        subtitleFontFamily: 'Tahoma, Geneva, sans-serif',
        subtitleFontSize: 16,
        subtitleOffsetY: 0,
        titleColor: '',
        subtitleColor: '',
        fullScreen: true,
        titleBold: true,
        titleItalic: true,
        titleAlign: 'right',
        subtitleBold: false,
        subtitleItalic: false,
        subtitleAlign: 'right',
        ctaBold: false,
        ctaItalic: false,
      };
    case 'campaignHero':
      return {
        imageUrl: galleryPlaceholder,
        title: 'A Ministry Built on Salvation',
        bodyText:
          'Write a paragraph that talks about your ministry here.\nYou can talk about your mission, vision, and philosophy.\n\nWrite a second paragraph to continue your story.',
        backgroundColor: '#4b2c1a',
        titleFontFamily: '',
        titleFontSize: 48,
        titleColor: '',
        titleBold: true,
        titleItalic: false,
        titleAlign: 'left',
        bodyFontFamily: '',
        bodyFontSize: 24,
        bodyColor: '',
        bodyBold: false,
        bodyItalic: false,
        bodyAlign: 'left',
        swapFrames: false,
      };
    case 'navigationTiles':
      return {
        tiles: [
          {
            imageUrl: galleryPlaceholder,
            title: 'Get To Knows Us',
            description: '',
            link: '/',
            buttonText: 'Learn More',
          },
          {
            imageUrl: galleryPlaceholder,
            title: 'Events',
            description: '',
            link: '/',
            buttonText: 'Learn More',
          },
          {
            imageUrl: galleryPlaceholder,
            title: 'Next Steps',
            description: '',
            link: '/',
            buttonText: 'Learn More',
          },
        ],
      };
    case 'marquee':
      return {
        title: '',
        speed: 30,
        items: [
          { imageUrl: galleryPlaceholder, link: '/' },
          { imageUrl: galleryPlaceholder, link: '/' },
          { imageUrl: galleryPlaceholder, link: '/' },
          { imageUrl: galleryPlaceholder, link: '/' },
          { imageUrl: galleryPlaceholder, link: '/' },
          { imageUrl: galleryPlaceholder, link: '/' },
        ],
      };
    case 'features':
      return {
        title: 'Our Work in the Community',
        subtitle:
          'Let your work speak for itself by presenting a portfolio. Share your previous projects here so clients can see your actual output.',
        items: [
          {
            imageUrl: galleryPlaceholder,
            title: 'Community Outreach',
            link: '/',
          },
          {
            imageUrl: galleryPlaceholder,
            title: 'Youth Bible Camp',
            link: '/',
          },
          {
            imageUrl: galleryPlaceholder,
            title: 'Mentorship Program',
            link: '/',
          },
        ],
      };
    case 'testimonials':
      return {
        title: 'What People Say',
        items: [
          {
            quote: 'This product changed how we work.',
            authorName: 'Jane Doe',
            authorTitle: 'CEO, Company',
            imageUrl: avatarPlaceholder,
          },
        ],
      };
    case 'faq':
      return {
        title: 'Frequently Asked Questions',
        items: [
          {
            question: 'How do I get started?',
            answer: 'Sign up and follow the guide.',
          },
          {
            question: 'Can I cancel anytime?',
            answer: 'Yes, no long-term commitment.',
          },
        ],
      };
    case 'cta':
      return {
        title: 'Ready to get started?',
        description: 'Join thousands of satisfied customers today.',
        buttonText: 'Sign Up Now',
        buttonLink: ABRASOFT_HUB_URL,
      };
    case 'footer':
      return {
        copyrightText: '© 2025 Your Company. All rights reserved.',
        links: [
          { label: 'About Us', url: '/' },
          { label: 'Ministries', url: '/' },
          { label: 'Events', url: '/' },
          { label: 'Sermons', url: '/' },
        ],
        socialLinks: [
          { iconUrl: '', url: '/', visible: true },
          { iconUrl: '', url: '/', visible: true },
          { iconUrl: '', url: '/', visible: true },
        ],
        brandTitle: 'Your Site Name',
        brandSubtitle: 'Chapter',
        brandDescription:
          "A community of faith, worship, and service committed to spreading God's love.",
        brandLogoUrl: '',
        contactAddress: '',
        contactPhone: '',
        contactEmail: '',
        connectTagline:
          'Follow us for updates, events, and inspirational content.',
      };
    case 'team':
      return {
        title: 'Our Team',
        items: [
          {
            name: 'Alex Smith',
            role: 'Founder',
            bio: 'Leading the vision.',
            imageUrl: avatarPlaceholder,
          },
          {
            name: 'Jordan Lee',
            role: 'Designer',
            bio: 'Making it beautiful.',
            imageUrl: avatarPlaceholder,
          },
        ],
      };
    case 'gallery':
      return {
        title: 'Gallery',
        items: [
          { url: galleryPlaceholder, title: '', description: '' },
          { url: galleryPlaceholder, title: '', description: '' },
          { url: galleryPlaceholder, title: '', description: '' },
          { url: galleryPlaceholder, title: '', description: '' },
          { url: galleryPlaceholder, title: '', description: '' },
          { url: galleryPlaceholder, title: '', description: '' },
        ],
      };
    default:
      return {};
  }
}

export function createSection(
  type: SectionTemplateKey,
  order: number,
): BuilderSection {
  return {
    id: `section-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    type,
    order,
    data: getDefaultData(type),
  };
}

function createDonateBodySection(order = 0): BuilderSection {
  const section = createSection('body', order);
  section.data = {
    ...section.data,
    variant: 'donate',
    title: 'Support Our Ministry',
    subtitle: 'We need your help.',
    body: 'Your giving helps us continue outreach, discipleship, and community care.',
    donateImageUrl: '',
    donateSuggestedAmounts: '100,250,500,1000',
    donateBankOptions: 'GCash,BDO,UnionBank',
    donateCurrency: 'PHP',
    donateMode: 'one-time',
    donateButtonLabel: 'Give Now',
    donateNote: 'Secure online giving. Thank you for your generosity.',
    thankYouTitle: 'Thank you for your gift!',
    thankYouSubtitle: 'Your donation will help local wildlife thrive.',
    thankYouReceiptText:
      "We've sent your donation receipt to [donor email address]",
    thankYouShareFacebookLabel: 'Share on Facebook',
    thankYouShareInstagramLabel: 'Share on Instagram',
  };
  return section;
}

export function createDefaultWebsiteBuilderState(): WebsiteBuilderState {
  const defaultPages: WebsiteBuilderPage[] = [
    {
      id: 'page-default-home',
      title: 'Home',
      slug: 'home',
      bodySections: [createSection('body', 0)],
    },
    {
      id: 'page-default-about',
      title: 'About Us',
      slug: 'about-us',
      bodySections: [createSection('body', 0)],
    },
    {
      id: 'page-default-visit-us',
      title: 'Visit Us',
      slug: 'visit-us',
      bodySections: [createSection('body', 0)],
    },
    {
      id: 'page-default-donate',
      title: 'Donate',
      slug: 'donate',
      bodySections: [createDonateBodySection(0)],
    },
  ];

  const defaultSectionOrder: SectionTemplateKey[] = [
    'header',
    'hero',
    'campaignHero',
    'features',
    'navigationTiles',
    'team',
    'cta',
    'footer',
  ];

  const defaultSections = defaultSectionOrder.map((type, order) =>
    createSection(type, order),
  );

  // Customize the second Campaign Hero to match the requested default design.
  const campaignSections = defaultSections.filter(
    (section) => section.type === 'campaignHero',
  );
  const secondCampaign = campaignSections[1];
  if (secondCampaign) {
    secondCampaign.data = {
      ...secondCampaign.data,
      title: 'Off the Streets: Our Efforts to End Homelessness',
      bodyText:
        '"Off the Streets: Our Efforts to End Homelessness" reflects our commitment to restoring dignity, hope, and stability to those in need.\n\nThrough compassionate outreach, practical support, and long-term programs, we work to help individuals transition from the streets into safe, sustainable living.\n\nBy addressing both immediate needs and root causes, we strive to create lasting change and rebuild lives within a caring community.',
      backgroundColor: '#29473f',
      titleFontFamily: "'Trebuchet MS', 'Segoe UI', sans-serif",
      titleFontSize: 27,
      titleBold: true,
      titleItalic: false,
      titleAlign: 'center',
      bodyFontFamily: '',
      bodyFontSize: 21,
      bodyBold: false,
      bodyItalic: true,
      bodyAlign: 'left',
      swapFrames: true,
    };
  }

  return {
    sections: defaultSections,
    pageBackgroundUrl: '',
    companyDomain: '',
    pages: defaultPages,
  };
}

export const BUILDER_STORAGE_KEY = 'website-builder-state';
